import React, { useEffect, useMemo, useState } from 'react';
import MyButton from '../../../components/MyButton';
import SearchInput from '../../../components/SearchInput';
import MyLoading from '../../../components/MyLoading';
import CarouselPhotosField from '../../../components/CarouselPhotosField';
import {
  CITY_MODES,
  SELECTION_MODES,
  fetchCitiesOfCountryCached,
  getEventConfigIssues,
  isValidOwnerId,
} from '../../../helpers/eventsBulkAutofill';

function Segment({ options, value, onChange }) {
  return (
    <div className="bulk_offers_segment">
      {options.map((option) => (
        <div
          key={String(option.value)}
          className={`bulk_offers_segment_item ${value === option.value ? 'active' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </div>
      ))}
    </div>
  );
}

function SetupBlock({ title, hint, children }) {
  return (
    <div className="bulk_offers_setup_block">
      <p className="bulk_offers_setup_block_title">{title}</p>
      {hint ? <p className="bulk_offers_field_note">{hint}</p> : null}
      {children}
    </div>
  );
}

function BulkSetupPanel({
  config,
  setConfig,
  categories,
  countries,
  staticLoading,
  locked,
  onDownload,
  downloadLoading,
  downloadError,
}) {
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const isSingleCountry = config.countryMode === SELECTION_MODES.single;
  const isSingleCategory = config.categoryMode === SELECTION_MODES.single;
  const issues = useMemo(() => getEventConfigIssues(config), [config]);

  useEffect(() => {
    let cancelled = false;
    const countryId = config.country?._id;
    if (!countryId) {
      setCities([]);
      return undefined;
    }
    setCitiesLoading(true);
    fetchCitiesOfCountryCached(countryId)
      .then((list) => { if (!cancelled) setCities(list); })
      .catch(() => { if (!cancelled) setCities([]); })
      .finally(() => { if (!cancelled) setCitiesLoading(false); });
    return () => { cancelled = true; };
  }, [config.country?._id]);

  const patchConfig = (patch) => setConfig((prev) => ({ ...prev, ...patch }));
  const ownerIdError = config.ownerId && !isValidOwnerId(config.ownerId) ? 'ID владельца — 24 символа' : '';

  if (staticLoading) {
    return (
      <div className="bulk_offers_panel">
        <MyLoading />
      </div>
    );
  }

  return (
    <div className="bulk_offers_setup">
      <div className="bulk_offers_panel bulk_offers_setup_intro">
        <p className="bulk_offers_panel_title">Как это работает</p>
        <ol className="bulk_offers_steps">
          <li>Выберите, что будет одинаковым у всех мероприятий: страна, город, категория, владелец, фото по ссылке.</li>
          <li>Скачайте файл для ИИ — в нём режим и справочники (категории, города, языки).</li>
          <li>Отправьте файл модели, затем присылайте данные о мероприятиях.</li>
          <li>Напишите «Собери файл» и загрузите ответ справа. Появятся карточки: их можно править и создавать пачкой.</li>
        </ol>
        <p className="bulk_offers_notice">
          Всё, что зафиксировано в режиме, применяется ко всем мероприятиям. В карточках такие поля закрыты.
          Фото можно дать файлом или ссылкой здесь, в карточке или получить ссылки от модели в photo_urls.
        </p>
      </div>

      <div className="bulk_offers_panel">
        <p className="bulk_offers_panel_title">Режим работы</p>
        {locked ? (
          <p className="bulk_offers_notice">
            Режим закреплён: по нему уже разобраны карточки. Чтобы поменять его, очистите список.
          </p>
        ) : null}

        <fieldset className="bulk_offers_setup_body" disabled={locked}>
          <SetupBlock title="Страна">
            <Segment
              options={[
                { value: SELECTION_MODES.all, label: 'Разные страны' },
                { value: SELECTION_MODES.single, label: 'Одна страна для всех' },
              ]}
              value={config.countryMode}
              onChange={(countryMode) => patchConfig({
                countryMode,
                country: countryMode === SELECTION_MODES.single ? config.country : null,
                city: null,
                cityMode: CITY_MODES.perOffer,
              })}
            />
            {isSingleCountry ? (
              <select
                className="bulk_offers_select"
                value={config.country?._id || ''}
                onChange={(e) => patchConfig({
                  country: countries.find((item) => item._id === e.target.value) || null,
                  city: null,
                  cityMode: CITY_MODES.perOffer,
                })}
              >
                <option value="">— Выберите страну —</option>
                {countries.map((item) => (
                  <option key={item._id} value={item._id}>{item.name}</option>
                ))}
              </select>
            ) : (
              <p className="bulk_offers_field_note">
                Города в файл не попадут — их нужно будет выбрать в карточке каждого мероприятия.
              </p>
            )}
          </SetupBlock>

          {isSingleCountry && config.country ? (
            <SetupBlock title="Город">
              <Segment
                options={[
                  { value: CITY_MODES.perOffer, label: 'У каждого свой' },
                  { value: CITY_MODES.single, label: 'Один для всех' },
                  { value: CITY_MODES.allCitiesOfCountry, label: 'Все города страны' },
                ]}
                value={config.cityMode}
                onChange={(cityMode) => patchConfig({
                  cityMode,
                  city: cityMode === CITY_MODES.single ? config.city : null,
                })}
              />
              {config.cityMode === CITY_MODES.single ? (
                <select
                  className="bulk_offers_select"
                  value={config.city?._id || ''}
                  onChange={(e) => patchConfig({
                    city: cities.find((item) => item._id === e.target.value) || null,
                  })}
                  disabled={citiesLoading}
                >
                  <option value="">{citiesLoading ? 'Загрузка городов…' : '— Выберите город —'}</option>
                  {cities.map((item) => (
                    <option key={item._id} value={item._id}>{item.name}</option>
                  ))}
                </select>
              ) : null}
            </SetupBlock>
          ) : null}

          <SetupBlock title="Категория">
            <Segment
              options={[
                { value: SELECTION_MODES.all, label: 'Разные категории' },
                { value: SELECTION_MODES.single, label: 'Одна для всех' },
              ]}
              value={config.categoryMode}
              onChange={(categoryMode) => patchConfig({
                categoryMode,
                category: categoryMode === SELECTION_MODES.single ? config.category : null,
              })}
            />
            {isSingleCategory ? (
              <select
                className="bulk_offers_select"
                value={config.category?._id || ''}
                onChange={(e) => patchConfig({
                  category: categories.find((item) => item._id === e.target.value) || null,
                })}
              >
                <option value="">— Выберите категорию —</option>
                {categories.map((item) => (
                  <option key={item._id} value={item._id}>{item.name}</option>
                ))}
              </select>
            ) : (
              <p className="bulk_offers_field_note">
                Модель подберёт категорию каждому мероприятию из справочника.
              </p>
            )}
          </SetupBlock>

          <SetupBlock title="ID владельца" hint="Один владелец на все мероприятия — 24 символа.">
            <SearchInput
              wrapperClassName="bulk_offers_input"
              withLeftIcon={false}
              placeholder="ID владельца"
              value={config.ownerId}
              onChange={(e) => patchConfig({ ownerId: typeof e === 'string' ? e : e.target.value })}
              shouldValueConsiderAsFocus={false}
              error={ownerIdError}
            />
          </SetupBlock>

          <SetupBlock
            title="Фото"
            hint="Необязательно. Эти файлы и ссылки проставятся каждому мероприятию. Ссылку сервер не скачивает. Без фото мероприятие создать нельзя — можно добавить позже в карточке."
          >
            <CarouselPhotosField
              value={config.photos || []}
              onChange={(photos) => patchConfig({ photos })}
              max={15}
            />
          </SetupBlock>
        </fieldset>

        <MyButton
          text="Скачать файл для ИИ"
          onClick={onDownload}
          loading={downloadLoading}
          disabled={Boolean(issues.length)}
          className="bulk_offers_wide_button"
        />
        {issues.length ? (
          <ul className="bulk_offers_issues">
            {issues.map((issue) => <li key={issue}>{issue}</li>)}
          </ul>
        ) : null}
        {downloadError ? <p className="bulk_offers_error">{downloadError}</p> : null}
      </div>
    </div>
  );
}

export default BulkSetupPanel;
